import * as React from "react";
import {
  Person20Filled,
  Box20Filled,
  QuestionCircle20Filled,
} from "@fluentui/react-icons";
import ChoiceCard from "../../schadenmeldung/ChoiceCard";
import { useStepStyles } from "../../schadenmeldung/steps/stepStyles";
import { BIG_COPY, YES_NO_UNKNOWN } from "../copy";
import { type BigStepProps, toggleValue, patchAt, makeExtraPerson } from "../types";
import {
  StepHead,
  Block,
  FieldGrid,
  TextField,
  TextAreaField,
  NumberField,
  RadioGroup,
  CheckboxList,
  ContactBlock,
  RepeatCard,
  AddButton,
} from "../fields";

export function SonBetroffen({ answers, update }: BigStepProps) {
  const s = useStepStyles();
  const C = BIG_COPY.sonBetroffen;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <div className={s.grid3}>
        <ChoiceCard
          size="lg"
          icon={<Person20Filled style={{ fontSize: 28 }} />}
          label={C.options.mensch.label}
          helper={C.options.mensch.helper}
          selected={answers.sonBetroffen === "mensch"}
          onClick={() => update({ sonBetroffen: "mensch" })}
        />
        <ChoiceCard
          size="lg"
          icon={<Box20Filled style={{ fontSize: 28 }} />}
          label={C.options.sache.label}
          helper={C.options.sache.helper}
          selected={answers.sonBetroffen === "sache"}
          onClick={() => update({ sonBetroffen: "sache" })}
        />
        <ChoiceCard
          size="lg"
          icon={<QuestionCircle20Filled style={{ fontSize: 28 }} />}
          label={C.options.sonstiges.label}
          helper={C.options.sonstiges.helper}
          selected={answers.sonBetroffen === "sonstiges"}
          onClick={() => update({ sonBetroffen: "sonstiges" })}
        />
      </div>
    </div>
  );
}

export function SonWhere({ answers, update }: BigStepProps) {
  const C = BIG_COPY.sonWhere;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <FieldGrid>
          <TextField label={C.plz} value={answers.sonPlz} onChange={(v) => update({ sonPlz: v })} inputMode="numeric" optional />
          <TextField label={C.ort} value={answers.sonOrt} onChange={(v) => update({ sonOrt: v })} optional />
        </FieldGrid>
        <TextField label={C.strasse} value={answers.sonStrasse} onChange={(v) => update({ sonStrasse: v })} optional />
      </Block>
    </div>
  );
}

function SacheDetails({ answers, update }: BigStepProps) {
  const C = BIG_COPY.sonSache;
  return (
    <Block>
      <TextAreaField label={C.beschreibung} value={answers.sonSacheBeschreibung} onChange={(v) => update({ sonSacheBeschreibung: v })} placeholder={C.beschreibungHint} />
      <RadioGroup label={C.eigentuemerLabel} options={C.eigentuemerOptions} value={answers.sonSacheEigentuemer} onChange={(v) => update({ sonSacheEigentuemer: v })} columns />
      {answers.sonSacheEigentuemer === "fremd" && (
        <ContactBlock value={answers.sonSacheFremdContact} onChange={(patch) => update({ sonSacheFremdContact: { ...answers.sonSacheFremdContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      )}
      <TextField label={C.ort} value={answers.sonSacheOrt} onChange={(v) => update({ sonSacheOrt: v })} optional />
      <TextAreaField label={C.hergang} value={answers.sonSacheHergang} onChange={(v) => update({ sonSacheHergang: v })} />
      <NumberField label={C.hoehe} value={answers.sonSacheHoehe} onChange={(v) => update({ sonSacheHoehe: v })} suffix="EUR" optional />
      <RadioGroup label={C.zeugen} options={YES_NO_UNKNOWN} value={answers.sonSacheZeugen} onChange={(v) => update({ sonSacheZeugen: v })} columns />
      {answers.sonSacheZeugen === "ja" && (
        <ContactBlock value={answers.sonSacheZeugenContact} onChange={(patch) => update({ sonSacheZeugenContact: { ...answers.sonSacheZeugenContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      )}
      <CheckboxList label={C.belegeLabel} options={C.belegeOptions} values={answers.sonSacheBelege} onToggle={(v) => update({ sonSacheBelege: toggleValue(answers.sonSacheBelege, v) })} columns />
    </Block>
  );
}

function MenschDetails({ answers, update }: BigStepProps) {
  const C = BIG_COPY.sonMensch;
  return (
    <Block>
      <RadioGroup label={C.werLabel} options={C.werOptions} value={answers.sonMenschWer} onChange={(v) => update({ sonMenschWer: v })} columns />
      {answers.sonMenschWer === "nein" && (
        <TextAreaField label={C.rolle} value={answers.sonMenschRolle} onChange={(v) => update({ sonMenschRolle: v })} />
      )}
      <ContactBlock value={answers.sonMenschContact} onChange={(patch) => update({ sonMenschContact: { ...answers.sonMenschContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      <TextAreaField label={C.hergang} value={answers.sonMenschHergang} onChange={(v) => update({ sonMenschHergang: v })} />
      <TextField label={C.verletzung} value={answers.sonMenschVerletzung} onChange={(v) => update({ sonMenschVerletzung: v })} optional />
      <RadioGroup label={C.arbeitsunfaehig} options={YES_NO_UNKNOWN} value={answers.sonMenschArbeitsunfaehig} onChange={(v) => update({ sonMenschArbeitsunfaehig: v })} columns />

      <RadioGroup
        label={C.weitereLabel}
        options={YES_NO_UNKNOWN.filter((o) => o.value !== "unbekannt")}
        value={answers.sonMenschWeitere.length > 0 ? "ja" : "nein"}
        onChange={(v) =>
          update({ sonMenschWeitere: v === "ja" && answers.sonMenschWeitere.length === 0 ? [makeExtraPerson()] : v === "nein" ? [] : answers.sonMenschWeitere })
        }
        columns
      />
      {answers.sonMenschWeitere.map((wp, i) => (
        <RepeatCard
          key={i}
          title={`Weitere Person ${i + 1}`}
          onRemove={() => update({ sonMenschWeitere: answers.sonMenschWeitere.filter((_, j) => j !== i) })}
        >
          <ContactBlock value={wp.contact} onChange={(patch) => update({ sonMenschWeitere: patchAt(answers.sonMenschWeitere, i, { contact: { ...wp.contact, ...patch } }) })} show={{ address: true, emailOptional: true }} />
          <TextField label={C.verletzung} value={wp.verletzung} onChange={(v) => update({ sonMenschWeitere: patchAt(answers.sonMenschWeitere, i, { verletzung: v }) })} optional />
        </RepeatCard>
      ))}
      {answers.sonMenschWeitere.length > 0 && (
        <AddButton label="Weitere verletzte Person" onClick={() => update({ sonMenschWeitere: [...answers.sonMenschWeitere, makeExtraPerson()] })} />
      )}

      <RadioGroup label={C.zeugen} options={YES_NO_UNKNOWN} value={answers.sonMenschZeugen} onChange={(v) => update({ sonMenschZeugen: v })} columns />
      {answers.sonMenschZeugen === "ja" && (
        <ContactBlock value={answers.sonMenschZeugenContact} onChange={(patch) => update({ sonMenschZeugenContact: { ...answers.sonMenschZeugenContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      )}
    </Block>
  );
}

function SonstDetails({ answers, update }: BigStepProps) {
  const C = BIG_COPY.sonSonst;
  return (
    <Block>
      <TextAreaField label={C.hergang} value={answers.sonSonstHergang} onChange={(v) => update({ sonSonstHergang: v })} placeholder={C.hergangHint} />
      <RadioGroup label={C.werLabel} options={C.werOptions} value={answers.sonSonstWer} onChange={(v) => update({ sonSonstWer: v })} columns />
      {(answers.sonSonstWer === "kunde" || answers.sonSonstWer === "fremd") && (
        <ContactBlock value={answers.sonSonstBetroffenContact} onChange={(patch) => update({ sonSonstBetroffenContact: { ...answers.sonSonstBetroffenContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      )}
      <NumberField label={C.hoehe} value={answers.sonSonstHoehe} onChange={(v) => update({ sonSonstHoehe: v })} suffix="EUR" optional />
      <RadioGroup label={C.anspruch} options={YES_NO_UNKNOWN} value={answers.sonSonstAnspruch} onChange={(v) => update({ sonSonstAnspruch: v })} columns />
      <RadioGroup label={C.zeugen} options={YES_NO_UNKNOWN} value={answers.sonSonstZeugen} onChange={(v) => update({ sonSonstZeugen: v })} columns />
      {answers.sonSonstZeugen === "ja" && (
        <ContactBlock value={answers.sonSonstZeugenContact} onChange={(patch) => update({ sonSonstZeugenContact: { ...answers.sonSonstZeugenContact, ...patch } })} show={{ address: true, emailOptional: true }} />
      )}
    </Block>
  );
}

export function SonDetails(props: BigStepProps) {
  const { answers } = props;
  const titleMap: Record<string, string> = {
    sache: BIG_COPY.sonSache.title,
    mensch: BIG_COPY.sonMensch.title,
    sonstiges: BIG_COPY.sonSonst.title,
  };
  const t = answers.sonBetroffen;
  return (
    <div>
      <StepHead eyebrow="Details" title={t ? titleMap[t] : "Details"} />
      {t === "sache" && <SacheDetails {...props} />}
      {t === "mensch" && <MenschDetails {...props} />}
      {t === "sonstiges" && <SonstDetails {...props} />}
    </div>
  );
}
